import { useState } from 'react';
import { Alert } from 'react-native';
import {
  Button,
  H1,
  Input,
  Paragraph,
  Text,
  XStack,
  YStack,
} from 'tamagui';
import { Screen, SkeletonList } from '@mrrmlab/ui';
import { PACKAGES_AUTO_TODO_LIST_TITLE, type List, type ListType } from '@mrrmlab/shared-types';
import { useCreateList, useDeleteList, useLists, useReorderLists } from '../hooks';
import { useStores } from '../stores-hooks';

const TYPE_OPTIONS: readonly { value: ListType; label: string }[] = [
  { value: 'shopping', label: 'Einkaufen' },
  { value: 'todo', label: 'Todo' },
  { value: 'notes', label: 'Notizen' },
];

export interface ListsOverviewScreenProps {
  /** Called when the user taps a list — apps route to a type-specific detail screen. */
  onOpenList: (list: List) => void;
  /** Optional link to the store management screen. */
  onManageStores?: () => void;
  /** When set, only lists of this type are shown and create-form is fixed to it. */
  typeFilter?: ListType;
}

export function ListsOverviewScreen({ onOpenList, onManageStores, typeFilter }: ListsOverviewScreenProps) {
  const listsQuery = useLists();
  const storesQuery = useStores();
  const createList = useCreateList();
  const deleteList = useDeleteList();
  const reorderLists = useReorderLists();
  const [title, setTitle] = useState('');
  const [type, setType] = useState<ListType>(typeFilter ?? 'notes');
  const [storeId, setStoreId] = useState<string | undefined>(undefined);
  const [plannedDate, setPlannedDate] = useState('');

  const submit = () => {
    const trimmed = title.trim();
    if (!trimmed) return;
    const trimmedDate = plannedDate.trim();
    createList.mutate(
      {
        title: trimmed,
        type,
        storeId: storeId ?? undefined,
        ...(type === 'shopping' && trimmedDate ? { plannedDate: trimmedDate } : {}),
      },
      {
        onSuccess: () => {
          setTitle('');
          setPlannedDate('');
        },
      },
    );
  };

  const stores = storesQuery.data ?? [];

  return (
    <Screen scrollable>
      <XStack alignItems="center" gap="$3">
        <H1 flex={1}>Listen</H1>
        {onManageStores ? (
          <Button size="$2" chromeless onPress={onManageStores}>
            Geschäfte
          </Button>
        ) : null}
      </XStack>

      <YStack gap="$2">
        <XStack gap="$2" alignItems="center">
          <Input
            flex={1}
            placeholder="Listenname"
            value={title}
            onChangeText={setTitle}
            onSubmitEditing={submit}
          />
          <Button
            disabled={createList.isPending || title.trim().length === 0}
            onPress={submit}
          >
            Hinzufügen
          </Button>
        </XStack>
        {typeFilter ? null : (
          <XStack gap="$2" flexWrap="wrap">
            {TYPE_OPTIONS.map((opt) => (
              <Button
                key={opt.value}
                size="$2"
                theme={type === opt.value ? 'active' : undefined}
                onPress={() => {
                  setType(opt.value);
                  if (opt.value !== 'shopping') setStoreId(undefined);
                }}
              >
                {opt.label}
              </Button>
            ))}
          </XStack>
        )}
        {type === 'shopping' ? (
          <XStack gap="$2" alignItems="center">
            <Text fontSize="$2" opacity={0.7} width={60}>Datum:</Text>
            <Input
              flex={1}
              placeholder="YYYY-MM-DD (optional)"
              value={plannedDate}
              onChangeText={setPlannedDate}
              inputMode="numeric"
            />
          </XStack>
        ) : null}
        {type === 'shopping' && stores.length > 0 ? (
          <XStack gap="$2" flexWrap="wrap" alignItems="center">
            <Text fontSize="$2" opacity={0.7}>Store:</Text>
            <Button
              size="$2"
              theme={storeId === undefined ? 'active' : undefined}
              onPress={() => setStoreId(undefined)}
            >
              Keines
            </Button>
            {stores.map((s) => (
              <Button
                key={s.id}
                size="$2"
                theme={storeId === s.id ? 'active' : undefined}
                onPress={() => setStoreId(s.id)}
              >
                {s.name}
              </Button>
            ))}
          </XStack>
        ) : null}
      </YStack>

      {listsQuery.isPending ? (
        <SkeletonList count={5} />
      ) : listsQuery.isError ? (
        <Paragraph color="$red10">
          Listen konnten nicht geladen werden: {listsQuery.error.message}
        </Paragraph>
      ) : (() => {
        const allLists = listsQuery.data;
        const visibleLists = typeFilter
          ? allLists.filter((l) => l.type === typeFilter)
          : allLists;
        if (visibleLists.length === 0) {
          return <Paragraph opacity={0.7}>Noch keine Liste. Oben hinzufügen.</Paragraph>;
        }
        const moveWithinVisible = (index: number, direction: -1 | 1) => {
          const target = index + direction;
          if (target < 0 || target >= visibleLists.length) return;
          const movingId = visibleLists[index]!.id;
          const neighborId = visibleLists[target]!.id;
          const orderedIds = allLists.map((l) => l.id);
          const i = orderedIds.indexOf(movingId);
          const j = orderedIds.indexOf(neighborId);
          if (i < 0 || j < 0) return;
          [orderedIds[i], orderedIds[j]] = [orderedIds[j]!, orderedIds[i]!];
          reorderLists.mutate({ orderedIds });
        };
        return (
        <YStack gap="$2">
          {visibleLists.map((list, idx) => {
            const store = list.storeId ? stores.find((s) => s.id === list.storeId) : undefined;
            const isAutoPackagesList = list.type === 'todo' && list.title === PACKAGES_AUTO_TODO_LIST_TITLE;
            const isFirst = idx === 0;
            const isLast = idx === visibleLists.length - 1;
            return (
              <XStack
                key={list.id}
                alignItems="center"
                gap="$3"
                paddingVertical="$2"
                paddingHorizontal="$3"
                borderRadius="$3"
                backgroundColor="$background"
                borderWidth={1}
                borderColor="$borderColor"
                hoverStyle={{ backgroundColor: '$backgroundHover' }}
                pressStyle={{ opacity: 0.8 }}
                onPress={() => onOpenList(list)}
              >
                <TypeBadge type={list.type} />
                {isAutoPackagesList ? <AutoBadge /> : null}
                <YStack flex={1} gap="$1">
                  <Paragraph fontSize="$5">{list.title}</Paragraph>
                  {(store || list.plannedDate) ? (
                    <Text fontSize="$2" opacity={0.6}>
                      {[store?.name, list.plannedDate ? `📅 ${list.plannedDate}` : null]
                        .filter(Boolean)
                        .join(' · ')}
                    </Text>
                  ) : null}
                </YStack>
                <YStack gap="$1">
                  <Button
                    size="$2"
                    chromeless
                    disabled={isFirst || reorderLists.isPending}
                    aria-label="Nach oben"
                    onPress={(event) => {
                      event.stopPropagation?.();
                      moveWithinVisible(idx, -1);
                    }}
                  >
                    ↑
                  </Button>
                  <Button
                    size="$2"
                    chromeless
                    disabled={isLast || reorderLists.isPending}
                    aria-label="Nach unten"
                    onPress={(event) => {
                      event.stopPropagation?.();
                      moveWithinVisible(idx, 1);
                    }}
                  >
                    ↓
                  </Button>
                </YStack>
                <Button
                  size="$2"
                  chromeless
                  disabled={deleteList.isPending}
                  onPress={(event) => {
                    event.stopPropagation?.();
                    Alert.alert(
                      'Liste löschen',
                      `"${list.title}" wirklich löschen?`,
                      [
                        { text: 'Abbrechen', style: 'cancel' },
                        { text: 'Löschen', style: 'destructive', onPress: () => deleteList.mutate(list.id) },
                      ],
                    );
                  }}
                >
                  Löschen
                </Button>
              </XStack>
            );
          })}
        </YStack>
        );
      })()}
    </Screen>
  );
}

function TypeBadge({ type }: { type: ListType }) {
  return (
    <XStack
      paddingHorizontal="$2"
      paddingVertical="$1"
      borderRadius="$2"
      backgroundColor="$color3"
    >
      <Text fontSize="$2" textTransform="uppercase" letterSpacing={0.5}>
        {type}
      </Text>
    </XStack>
  );
}

// Markiert die per Tracking→Todo-Pipeline (#143/#144/#145) automatisch
// erzeugte 'Pakete'-Liste, damit User erkennen, dass diese Liste nicht manuell
// gepflegt wird (#329 Discoverability).
function AutoBadge() {
  return (
    <XStack
      paddingHorizontal="$2"
      paddingVertical="$1"
      borderRadius="$2"
      backgroundColor="$blue3"
      borderWidth={1}
      borderColor="$blue6"
    >
      <Text fontSize="$2" textTransform="uppercase" letterSpacing={0.5} color="$blue11">
        auto
      </Text>
    </XStack>
  );
}
